import { BigNumber, Container, FormattedNumber, Filters } from '../../../../components'
import { DateSelect } from '../../../../components/DateSelect'
import { FilterDrawer } from '../../../../components/FilterDrawer'
import { FilterDrawerButton } from '../../../../components/FilterDrawerButton'
import { FilterDrawerStack } from '../../../../components/FilterDrawerStack'
import { PeriodType, UUID } from '@indigohive/cogfy-types'
import { TasksHistoryCard } from './components/TasksHistoryCard'
import { TasksUsageChart } from './components/TasksUsageChart/TasksUsageChart'
import { useFilters, useTitle, useWorkspace } from '../../../../hooks'
import { useParams } from 'react-router-dom'
import { useState } from 'react'
import { useTasksUsageSum } from '../../../../hooks/use-tasks-usage-sum'
import { useTranslation } from 'react-i18next'
import { useWorkspaceTasksQuotaHistory } from '../../../../hooks/use-workspace-task-quota-history'

export function TasksUsagePage () {
  const params = useParams<{ workspace: string }>()
  const workspace = params.workspace!
  const getWorkspace = useWorkspace(workspace)
  const getWorkspaceTasksQuotaHistory = useWorkspaceTasksQuotaHistory(workspace)
  const tasksQuotaHistory = getWorkspaceTasksQuotaHistory.data ?? []
  const { t } = useTranslation()

  const [startDate, setStartDate] = useState<Date | undefined>(undefined)
  const [endDate, setEndDate] = useState<Date | undefined>(undefined)
  const [open, setOpen] = useState(false)
  const [periodType, setPeriodType] = useState<PeriodType>('today')
  const filtersHook = useFilters([
    { name: t('Collection'), valueLabel: t('All'), value: undefined }
  ])

  useTitle({
    title: t('Tasks usage'),
    loading: getWorkspace.isLoading,
    error: getWorkspace.isError
  })

  const collection = filtersHook.filters.find(f => f.name === t('Collection'))

  const {
    data: tasksUsage,
    isError: errorTasksUsage,
    isLoading: loadingTasksUsage,
    refetch: refetchTasksUsage
  } = useTasksUsageSum(workspace, {
    startDate,
    endDate,
    collectionId: collection?.value as UUID | undefined
  })

  return (
    <Container className="flex flex-col gap-6 !px-0">
      <div className='flex flex-row justify-end items-end gap-4 sticky top-0 z-10 border-b-2 bg-white py-3 px-2 mt-4'>
        <Filters filtersHook={filtersHook} />
        <FilterDrawerButton onClick={() => setOpen(true)} />
        <DateSelect
          className='!w-fit'
          setStartDate={setStartDate}
          setEndDate={setEndDate}
          periodType={periodType}
          setPeriodType={setPeriodType}
          setOpen={setOpen}
        />
      </div>
      <FilterDrawer open={open} setOpen={setOpen} >
        <FilterDrawerStack
          filtersHook={filtersHook}
          renderCollection
        />
      </FilterDrawer>
      <div className="flex flex-row gap-4 min-h-40">
        <BigNumber.Card
          className="!w-fit"
          loading={loadingTasksUsage}
          error={errorTasksUsage}
          onRefetchClick={() => { refetchTasksUsage() }}
        >
          <span>{t('workspaceUsagePage:Tasks in the selected period')}</span>
          <BigNumber.Number>
            <FormattedNumber value={tasksUsage || 0} />
          </BigNumber.Number>
        </BigNumber.Card>
      </div>
      <TasksUsageChart
        collectionId={collection?.value as UUID | undefined}
        endDate={endDate}
        startDate={startDate}
      />
      <div>
        <p className='text-2xl font-medium mb-4 border-b-1 pb-2'>{t('workspaceUsagePage:History')}</p>
        <TasksHistoryCard
          tasksUsageHistory={tasksQuotaHistory}
          loading={getWorkspaceTasksQuotaHistory.isLoading}
          error={getWorkspaceTasksQuotaHistory.error}
          onRefetch={() => getWorkspaceTasksQuotaHistory.refetch}
        />
      </div>
    </Container>
  )
}
